import { forwardRef, Ref, useEffect, useImperativeHandle, useRef, useState } from "react"
import { ExSelect, ExSelectHandler, ExSelectProps } from "../ExSelect"
import { ToggleButton, ToggleButtonGroup } from "@mui/material"
import { Equal, EqualNot } from "lucide-react"
import { getLocalStorage, setLocalStorage } from "../../commons/utils"

export interface ExSelectFilterHandler extends ExSelectHandler {
    applyFilter: (list: any[]) => any[]
}

type ExSelectFilterProps = Omit<ExSelectProps, 'id'> & { variant?: string, filterField?: string }

export const ExSelectFilter = forwardRef((props: ExSelectFilterProps, ref: Ref<ExSelectFilterHandler & any>) => {
    const [operator, setOperator] = useState('=')

    const exSelectRef = useRef<any>(null)
    const defaultValueRef = useRef<any>(null)

    useImperativeHandle(ref, () => ({
        applyFilter,
        getValue: exSelectRef.current?.getValue,
        setValue: exSelectRef.current?.setValue,
        getId: exSelectRef.current?.getId,
        validate: exSelectRef.current?.validate,
        setCustomOptions: exSelectRef.current?.setCustomOptions,
        getDescription: exSelectRef.current?.getDescription,
        setCustomValue: exSelectRef.current?.setCustomValue,
        setFilter: exSelectRef.current?.setFilter,
        clearFilter: exSelectRef.current?.clearFilter
    }))

    if (defaultValueRef.current === null) {
        const _list = getLocalStorage('ExSelectFilter_' + props.model.name.toLowerCase() + (props.variant || ''));
        const _operator = getLocalStorage('ExSelectFilter_op_' + props.model.name.toLowerCase() + (props.variant || ''))

        defaultValueRef.current = _list
        if (_operator) setOperator(_operator)
    }

    useEffect(() => {
        if (exSelectRef.current?.getValue()?.length)
            props.onAfterChange?.(exSelectRef.current?.getValue());

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [operator])

    const applyFilter = (list: any[]) => {
        const values = exSelectRef.current?.getValue()

        if (values && values.length > 0) {
            const _values = new Set(values.map((item: any) => item.value));
            list = list.filter((item: any) => _values.has(item[props.filterField || props.model.key]) ? operator === '=' : operator === '!=');
        }

        if (values) {
            setLocalStorage('ExSelectFilter_' + props.model.name.toLowerCase() + (props.variant || ''), values);
            setLocalStorage('ExSelectFilter_op_' + props.model.name.toLowerCase() + (props.variant || ''), operator);
        }

        return list
    }

    const handleOperator = (event: React.MouseEvent<HTMLElement>, _operator: string) => {
        setOperator(_operator)
    }

    return (
        <ExSelect
            {...props}
            LeftComponent={
                <ToggleButtonGroup
                    value={operator}
                    color="standard"
                    exclusive
                    size="small"
                    onChange={handleOperator}
                >
                    <ToggleButton value={'='}><Equal size={16} /></ToggleButton>
                    <ToggleButton value={'!='}><EqualNot size={16} /></ToggleButton>
                </ToggleButtonGroup>
            }
            ref={exSelectRef}
            isMulti={props.isMulti === undefined ? true : props.isMulti}
            label={props.label || props.model.name}
            id={props.model.name.toLowerCase()}
            defaultValue={defaultValueRef.current}
        />
    )
})